import { inject, Injectable } from '@angular/core';
import { ModelsTargetType } from '../../../../api/core/models';
import { Colours } from '../../../../base-styles/colours';
import { holdsActiveCustomModel } from '../../../../functions/client-model/client-node/holds-active-custom-model';
import { holdsActiveTransitionTargetData } from '../../../../functions/client-model/client-node/holds-active-transition-target-data';
import { holdsCustomModel } from '../../../../functions/client-model/client-node/holds-custom-model';
import { holdsInitialisedUtilisationTE } from '../../../../functions/client-model/client-node/holds-initialised-utilisation-te';
import { fillOutArray } from '../../../../functions/fill-out-array';
import { getUnit } from '../../../../functions/get-unit';
import { UI_STRINGS } from '../../../../functions/language/uistrings';
import { DASHBOARD_STRINGS } from '../../../../functions/language/uistrings-tolgee';
import { convertUnit } from '../../../../functions/prefixed-unit/convert-unit';
import { getTransitionYearsFromTimeline } from '../../../../functions/timeline/get-transition-years-from-timeline';
import { sumArray } from '../../../../functions/utilities/array-utility';
import { ClientNode } from '../../../../model/client-model/client-node';
import { Dict } from '../../../../model/dict';
import { Language } from '../../../../model/enums/language';
import { MeasurementSystem } from '../../../../model/enums/measurement-system';
import { WeightMetric } from '../../../../model/prefixed-unit/weight.metric';
import { StyledSeries } from '../../../../modules/diagram/compound-renderers/chart-renderer/chart/series/styled-series';
import { STATIC_DASHBOARD_IDS } from '../../../../public/dashboard/shared/dashboard.constants';
import { ClientBoardContainer } from '../../../../public/dashboard/shared/model/client-board-container';
import { DashboardColours } from '../../../../public/dashboard/shared/model/dashboard-colours';
import { DashboardNodeV4 } from '../../../../public/dashboard/shared/model/dashboard-node-v4/dashboard-node-v4';
import { DashboardTransitionDataV4 } from '../../../../public/dashboard/shared/model/dashboard-transition-data-v4';
import {
  DashboardV5CoreModule,
  DashboardV5EmissionsModule,
  DashboardV5IndicatorsModule,
  DashboardV5InterventionsModule,
  DashboardV5TransitionsModule,
} from '../../../../public/dashboard/store/dashboard-v5';
import { AppConfigService } from '../../../../services/app-config.service';
import {
  generateModelTranslationKey,
  ModelTranslationOutputType,
} from '../../../../services/climate-engine/ccc-model-utility/generate-model-translation-key';
import { FormatNumberService } from '../../../../services/format-number.service';
import { TranslationService } from '../../../../services/translation-service';
import { UnitConversionService } from '../../../../services/unit-conversion/unit-conversion.service';
import { getLeafNodes } from '../../functions/get-leaf-nodes';
import { getNodes } from '../../functions/get-nodes';
import { DashboardAdditionalData } from '../../model/dashboard-additional-data';
import { isDashboardReportedCalculationResult } from '../dashboard-calculation/is-dashboard-reported-calculation-result';
import { colourForSinkNode } from '../dashboard-v4/colour-for-emission-node-v42';
import { colourForNodeV5 } from '../dashboard-v4/colour-for-node-v5';
import { ActivityTree, createActivityTree } from '../dashboard-v4/create-activity-tree';
import { createEmissionNodes } from '../dashboard-v4/create-emission-nodes';
import { createTransitionTree } from '../dashboard-v4/create-transition-tree';
import { DiagramSeriesService } from '../diagram-series/diagram-series.service';
import { EmissionDataService } from '../emission-data/emission-data.service';
import { TransitionDataService } from '../transition-data/transition-data.service';
import { TransitionItem } from '../transition-data/transition-item';

@Injectable({
  providedIn: 'root',
})
export class DashboardV5Service {
  private _configService = inject(AppConfigService);
  private _diagramSeriesService = inject(DiagramSeriesService);
  private _emissionDataService = inject(EmissionDataService);
  private _transitionDataService = inject(TransitionDataService);
  private _translationService = inject(TranslationService);
  private _formatNumberService = inject(FormatNumberService);
  private _unitConversionService = inject(UnitConversionService);

  toDashboardCoreModule(board: any): DashboardV5CoreModule {
    const years = getTransitionYearsFromTimeline(board.boardSettings.timeline);
    const endYear = years[years.length - 1] ?? 0;
    const startYear = years[0] ?? 0;
    return {
      ...board,
      endYear,
      startYear,
      years,
      colours: {
        main: Colours.emissionColour,
        dark: Colours.emissionColourDark,
        light: Colours.emissionColour,
      },
    };
  }

  toDashboardEmissionsModule(
    {
      calculationResult,
      labels,
      hideEnergyModel,
      reportedEmissionsCalculationResult,
      activityTree,
    }: DashboardAdditionalData,
    board: ClientBoardContainer,
    isPanorama: boolean
  ): DashboardV5EmissionsModule {
    const timeline = board.boardSettings.timeline;
    const emissionsTree: ActivityTree = createActivityTree(
      board.id,
      calculationResult,
      activityTree!,
      isPanorama,
      this._configService.baseUrlFiles
    );
    const emissionData = this._emissionDataService.create(
      board,
      calculationResult,
      isDashboardReportedCalculationResult(reportedEmissionsCalculationResult)
        ? reportedEmissionsCalculationResult
        : { inventoryEmissions: {} },
      !!hideEnergyModel,
      emissionsTree
    );
    const transitionData = this._transitionDataService.create(board, calculationResult);
    const absoluteNodeContributions: Dict<number> = this._getAbsoluteNodeContributions(transitionData);
    const nodes = this._createNodes(
      board,
      isPanorama ? emissionData.nodeEndYearContributions : emissionData.nodeCumulativeContributions,
      absoluteNodeContributions
    );

    const lang: Language = board.boardSettings.internationalisation.defaultLanguage;
    const emissionLabelFormatter = (value: number, parentValue: number) => {
      const unit = getUnit(parentValue / 10);
      const convertedValue = convertUnit(WeightMetric.Gram, unit, value);
      const largeMetric = unit === WeightMetric.MegaTonne || unit === WeightMetric.GigaTonne;
      const decimals = largeMetric ? 2 : 0;
      const formattedValue = this._formatNumberService.formatWithBoardLanguage(lang, convertedValue, decimals);
      return this._translationService.formatWithUnit(
        lang,
        formattedValue,
        this._unitConversionService.transformLabelUnit(
          unit,
          board.boardSettings.measurementSystem ?? MeasurementSystem.Metric
        )
      );
    };
    const emissionNodes = createEmissionNodes(
      emissionsTree,
      timeline.scenarioStartYear,
      emissionData.emissionScenarioData,
      emissionLabelFormatter
    );
    //we show dots if there is at least one interim goal set.
    // temporary solution until the styling is created in the diagram component.
    const showGoalDots: boolean = Object.values(
      board.boardSettings.reductionGoals?.[0]?.additionalGoalsByYear ?? {}
    ).some((v) => v !== undefined);
    return {
      ...board,
      emissionChildNodes: emissionsTree.children,
      emissionNodes,
      emissionScenarioDiagramSeries: this._diagramSeriesService.createEmissionScenarioDiagramSeries(
        board.id,
        emissionNodes,
        this._createSinkData(lang),
        emissionData.emissionScenarioData,
        showGoalDots
      ),
      labelsV4: { ...labels, ...this._createLabelsForCustomModels(board) },
      nodeCumulativeAbatements: emissionData.nodeCumulativeAbatements,
      nodeCumulativeContributions: emissionData.nodeCumulativeContributions,
      nodeEndYearAbatements: emissionData.nodeEndYearAbatements,
      nodeEndYearContributions: emissionData.nodeEndYearContributions,
      nodes,
      transitionPathwaySeries: this._diagramSeriesService.createTransitionPathwayDataSeries(
        board.id,
        nodes,
        emissionData.mainTransitionPathway,
        emissionData.transitionPathwayData
      ),
    };
  }

  toDashboardIndicatorsModule(
    {
      calculationResult,
      hideEnergyModel,
      labels,
      reportedEmissionsCalculationResult,
      activityTree,
    }: DashboardAdditionalData,

    board: ClientBoardContainer,
    isPanorama: boolean
  ): DashboardV5IndicatorsModule {
    const emissionsTree: ActivityTree = createActivityTree(
      board.id,
      calculationResult,
      activityTree!,
      isPanorama,
      this._configService.baseUrlFiles
    );
    const emissionData = this._emissionDataService.create(
      board,
      calculationResult,
      isDashboardReportedCalculationResult(reportedEmissionsCalculationResult)
        ? reportedEmissionsCalculationResult
        : { inventoryEmissions: {} },
      !!hideEnergyModel,
      emissionsTree
    );
    const transitionData = this._transitionDataService.create(board, calculationResult);
    const absoluteNodeContributions: Dict<number> = this._getAbsoluteNodeContributions(transitionData);
    const isV5 = true;
    const nodes = this._createNodes(
      board,
      isPanorama ? emissionData.nodeEndYearContributions : emissionData.nodeCumulativeContributions,
      absoluteNodeContributions
    );
    const transitionTree = createTransitionTree(board.id, board.childNodes, nodes);
    const styledTransitionTargetSeries = this._diagramSeriesService.createTransitionTargetDataSeries(
      nodes,
      transitionData,
      isV5
    );

    return {
      ...board,
      nodes,
      transitionPathwaySeries: this._diagramSeriesService.createTransitionPathwayDataSeries(
        board.id,
        nodes,
        emissionData.mainTransitionPathway,
        emissionData.transitionPathwayData
      ),
      transitionTree,
      transitionTargetData: this._createTransitionTargetData(styledTransitionTargetSeries),
      nodeCumulativeContributions: emissionData.nodeCumulativeContributions,
      nodeCumulativeAbatements: emissionData.nodeCumulativeAbatements,
      nodeEndYearContributions: emissionData.nodeEndYearContributions,
      nodeEndYearAbatements: emissionData.nodeEndYearAbatements,
      labelsV4: { ...labels, ...this._createLabelsForCustomModels(board) },
    };
  }

  toDashboardInterventionsModule(board: ClientBoardContainer): DashboardV5InterventionsModule {
    const nodes = this._createNodes(board, {}, {});
    return {
      ...board,
      nodes,
    };
  }

  toDashboardTransitionsModule(
    {
      calculationResult,
      reportedEmissionsCalculationResult,
      labels,
      hideEnergyModel,
      activityTree,
    }: DashboardAdditionalData,
    board: ClientBoardContainer,
    isPanorama: boolean
  ): DashboardV5TransitionsModule {
    const timeline = board.boardSettings.timeline;
    const emissionsTree: ActivityTree = createActivityTree(
      board.id,
      calculationResult,
      activityTree!,
      isPanorama,
      this._configService.baseUrlFiles
    );
    const emissionData = this._emissionDataService.create(
      board,
      calculationResult,
      isDashboardReportedCalculationResult(reportedEmissionsCalculationResult)
        ? reportedEmissionsCalculationResult
        : { inventoryEmissions: {} },
      !!hideEnergyModel,
      emissionsTree
    );
    const transitionData = this._transitionDataService.create(board, calculationResult);
    const absoluteNodeContributions: Dict<number> = this._getAbsoluteNodeContributions(transitionData);
    const isV5 = true;
    const nodes = this._createNodes(board, emissionData.nodeEndYearContributions, absoluteNodeContributions);
    const styledTransitionTargetSeries = this._diagramSeriesService.createTransitionTargetDataSeries(
      nodes,
      transitionData,
      isV5
    );
    const styledTransitionCarbonAbatementSeries = this._diagramSeriesService.createTransitionCarbonAbatementDataSeries(
      nodes,
      transitionData
    );
    const hasEnergyModel =
      emissionData.nodeCumulativeContributions.hasOwnProperty(STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_ID) ||
      emissionData.nodeEndYearContributions.hasOwnProperty(STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_ID);
    const totalCumulativeGapValue = Object.values(emissionData.mainTransitionPathway).reduce((total, item) => {
      return total + (item.gap ?? 0);
    }, 0);
    const hasCumulativeNetGap = totalCumulativeGapValue > 0;

    const energyCumulativeContribution =
      emissionData.nodeCumulativeContributions[STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_ID] ?? 0;
    const boardCumulativeContribution =
      board.childNodes.reduce((total, nodeId) => total + (emissionData.nodeCumulativeContributions[nodeId] ?? 0), 0) +
      energyCumulativeContribution;
    const cumulativeContributionGap = 100 - boardCumulativeContribution;
    const cumulativeGapPercentage = Math.max(0, cumulativeContributionGap);

    const energyEndYearContribution =
      emissionData.nodeEndYearContributions[STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_ID] ?? 0;
    const boardEndYearContribution =
      board.childNodes.reduce((total, nodeId) => total + (emissionData.nodeEndYearContributions[nodeId] ?? 0), 0) +
      energyEndYearContribution;
    const endYearContributionGap = 100 - boardEndYearContribution;
    const endYearGapPercentage = Math.max(0, endYearContributionGap);

    const lang: Language = board.boardSettings.internationalisation.defaultLanguage;
    const emissionLabelFormatter = (value: number, parentValue: number) => {
      const unit = getUnit(parentValue / 10);
      const convertedValue = convertUnit(WeightMetric.Gram, unit, value);
      const largeMetric = unit === WeightMetric.MegaTonne || unit === WeightMetric.GigaTonne;
      const decimals = largeMetric ? 2 : 0;
      const formattedValue = this._formatNumberService.formatWithBoardLanguage(lang, convertedValue, decimals);
      return this._translationService.formatWithUnit(
        lang,
        formattedValue,
        this._unitConversionService.transformLabelUnit(
          unit,
          board.boardSettings.measurementSystem ?? MeasurementSystem.Metric
        )
      );
    };
    const emissionNodes = createEmissionNodes(
      emissionsTree,
      timeline.scenarioStartYear,
      emissionData.emissionScenarioData,
      emissionLabelFormatter
    );
    //we show dots if there is at least one interim goal set.
    // temporary solution until the styling is created in the diagram component.
    const showGoalDots: boolean = Object.values(
      board.boardSettings.reductionGoals?.[0]?.additionalGoalsByYear ?? {}
    ).some((v) => v !== undefined);
    return {
      ...board,
      nodes,
      emissionNodes,
      emissionChildNodes: emissionsTree.children,
      emissionScenarioDiagramSeries: this._diagramSeriesService.createEmissionScenarioDiagramSeries(
        board.id,
        emissionNodes,
        this._createSinkData(lang),
        emissionData.emissionScenarioData,
        showGoalDots
      ),
      transitionPathwaySeries: this._diagramSeriesService.createTransitionPathwayDataSeries(
        board.id,
        nodes,
        emissionData.mainTransitionPathway,
        emissionData.transitionPathwayData
      ),
      transitionScenarioDiagramData: this._diagramSeriesService.createTransitionScenarioDiagramData(
        board.id,
        nodes,
        emissionData.mainTransitionScenarioData,
        emissionData.transitionScenarioData
      ),
      transitionTargetData: this._createTransitionTargetData(styledTransitionTargetSeries),
      transitionCarbonAbatementData: this._createTransitionTargetData(styledTransitionCarbonAbatementSeries),
      nodeCumulativeContributions: emissionData.nodeCumulativeContributions,
      nodeEndYearContributions: emissionData.nodeEndYearContributions,
      nodeCumulativeAbatements: emissionData.nodeCumulativeAbatements,
      nodeEndYearAbatements: emissionData.nodeEndYearAbatements,
      labelsV4: { ...labels, ...this._createLabelsForCustomModels(board) },
      energyNode: hasEnergyModel
        ? {
            parentId: board.id,
            id: STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_ID,
            colours: Colours.sectors.energy,
            contribution: energyEndYearContribution,
            fullContribution: emissionData.nodeEndYearContributions[STATIC_DASHBOARD_IDS.ENERGY_PRODUCTION_FULL_ID],
          }
        : undefined,
      showGapNode: hasCumulativeNetGap,
      cumulativeGapPercentage,
      endYearGapPercentage,
    };
  }

  private _createLabelsForCustomModels(board: ClientBoardContainer): Dict<{ transitionUnit: string }> {
    const labelsDict: Dict<{ transitionUnit: string }> = {};
    Object.entries(board.nodes).forEach(([nodeId, node]) => {
      if (node.nodeProperties?.customTargetModelData) {
        labelsDict[nodeId] = {
          transitionUnit: node.nodeProperties.customTargetModelData?.transitionUnit,
        };
      }
    });
    return labelsDict;
  }

  private _createNodes(
    board: ClientBoardContainer,
    nodeContributions: Dict<number>,
    absoluteNodeContributions: Dict<number>
  ): Dict<DashboardNodeV4> {
    const lang: Language = board.boardSettings.internationalisation.defaultLanguage;
    const parentIds: Dict<string> = this._createParentIdDict(board, getNodes(board));
    const nodes = board.nodes;
    const leafNodeNotQuantifiable = (nodeId: string) => {
      const node = nodes[nodeId];
      if (!holdsActiveTransitionTargetData(node) && holdsActiveCustomModel(node)) {
        const emissionData = fillOutArray(
          Object.values(node.nodeProperties.customTargetModelData!.chartData.emissions) ?? []
        );
        const abatementData = fillOutArray(
          Object.values(node.nodeProperties.customTargetModelData!.chartData.carbonAbatement) ?? []
        );
        return sumArray(emissionData.concat(abatementData)) === 0;
      }
      return false;
    };
    return Object.entries(nodes).reduce((dict: Dict<DashboardNodeV4>, [nodeId, node]) => {
      const leafNodes = getLeafNodes(board, node).map((n) => n.id);
      dict[nodeId] = {
        ...node,
        colours: colourForNodeV5(board, node),
        leafNodes,
        parentId: parentIds[node.id],
        contribution: nodeContributions[nodeId],
        notQuantifiable: leafNodes.length
          ? leafNodes.reduce((allIsAdaptation, leafId) => {
              return allIsAdaptation && leafNodeNotQuantifiable(leafId);
            }, true)
          : leafNodeNotQuantifiable(nodeId),
        texts: {
          transitionCarbonAbatementDiagramDescription: this._getTransitionCarbonAbatementDiagramDescription(
            lang,
            node,
            absoluteNodeContributions[nodeId]
          ),
          transitionTargetDiagramDescription: this._getTransitionTargetDiagramDescription(lang, node),
        },
      };
      return dict;
    }, {});
  }

  private _createParentIdDict(board: ClientBoardContainer, nodes: ClientNode[]): Dict<string> {
    return [board, ...nodes].reduce((ids: Dict<string>, item) => {
      if (item.childNodes) {
        item.childNodes.forEach((nodeId) => {
          ids[nodeId] = item.id;
        });
      }
      return ids;
    }, {});
  }

  private _createSinkData(lang: Language): { title: string; colours: DashboardColours } {
    return {
      title: this._translationService.translate({
        language: lang,
        key: DASHBOARD_STRINGS.REMOVAL,
        ns: 'dashboard',
      }),
      colours: colourForSinkNode(),
    };
  }

  private _createTransitionTargetData(styledSeries: Dict<StyledSeries[]>): Dict<DashboardTransitionDataV4> {
    const dict: Dict<DashboardTransitionDataV4> = {};
    Object.entries(styledSeries).forEach(([nodeId, series]) => {
      dict[nodeId] = {
        series,
      };
    });
    return dict;
  }

  private _getAbsoluteNodeContributions(
    transitionData: Dict<{
      [year: string]: TransitionItem;
    }>
  ): Dict<number> {
    return Object.entries(transitionData).reduce((obj: Dict<number>, [nodeId, node]) => {
      obj[nodeId] = sumArray(
        Object.keys(node)
          .map((year) => node[year].carbonAbatement)
          .filter((ca) => ca !== undefined) as number[]
      );
      return obj;
    }, {});
  }

  private _getTransitionCarbonAbatementDiagramDescription(
    lang: Language,
    node: ClientNode,
    nodeContribution: number
  ): string {
    let nodeTitle: string = '';
    if (holdsCustomModel(node)) {
      nodeTitle = node.title;
    } else if (holdsInitialisedUtilisationTE(node)) {
      const key = generateModelTranslationKey(
        ModelTranslationOutputType.TransitionElementTitle,
        node.nodeProperties.transitionElement!
      );
      nodeTitle = key ? this._translationService.translate(lang, key) : '';
    }
    return this._translationService.translate({
      language: lang,
      key: UI_STRINGS.DASHBOARD.TRANSITION_CARBON_ABATEMENT_DIAGRAM_DESCRIPTION,
      params: {
        reductionVal: convertUnit(WeightMetric.Gram, WeightMetric.KiloTonne, nodeContribution, 2),
        title: nodeTitle,
      },
    });
  }

  private _getTransitionTargetDiagramDescription(lang: Language, node: ClientNode): string {
    let description: string = '';

    if (holdsCustomModel(node)) {
      description = node.nodeProperties?.customTargetModelData?.diagramDescription ?? '';
    } else if (holdsInitialisedUtilisationTE(node)) {
      const key = generateModelTranslationKey(
        node.nodeProperties.transitionTargetData?.transitionProperties?.weightType === ModelsTargetType.ParameterUpdate
          ? ModelTranslationOutputType.TransitionElementParameterUpdateLeverDescription
          : ModelTranslationOutputType.TransitionElementActvityShiftDescription,
        node.nodeProperties.transitionElement!
      );

      description = key ? this._translationService.translate(lang, key) : '';
    }
    return description;
  }
}
